import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Mail, MessageSquare, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => (s ? new Date(s).toLocaleString() : '—');

const SuperAdminDeliveryLogs = () => {
  const [data, setData] = useState({ items: [], counts: { sent: 0, failed: 0 } });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [channel, setChannel] = useState('all');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (channel !== 'all') params.channel = channel;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/delivery-logs`, { params, withCredentials: true });
      setData(r.data);
    } catch { toast.error('Failed to load delivery log'); } finally { setLoading(false); }
  }, [status, channel, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const tab = (val, cur, set, label, testid) => (
    <button key={val} onClick={() => set(val)} data-testid={testid}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest border ${cur === val ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
      {label}
    </button>
  );

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="delivery-logs-page" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Delivery Log</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">Every email and SMS the platform tried to send, with the provider's reason when it failed — so a bounced activation email is never silent.</p>
          </div>
          <div className="flex gap-3 text-sm">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800" data-testid="delivery-count-sent"><b>{data.counts.sent}</b> sent</div>
            <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-red-800" data-testid="delivery-count-failed"><b>{data.counts.failed}</b> failed</div>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {tab('all', status, setStatus, 'All', 'delivery-filter-all')}
          {tab('sent', status, setStatus, 'Sent', 'delivery-filter-sent')}
          {tab('failed', status, setStatus, 'Failed', 'delivery-filter-failed')}
          <span className="mx-1 text-slate-300">|</span>
          {tab('all', channel, setChannel, 'Email + SMS', 'delivery-channel-all')}
          {tab('email', channel, setChannel, 'Email', 'delivery-channel-email')}
          {tab('sms', channel, setChannel, 'SMS', 'delivery-channel-sms')}
          <div className="relative ml-auto">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject" data-testid="delivery-search"
                   className="pl-8 pr-3 py-2 border border-slate-300 rounded-md text-sm w-64" />
          </div>
          <button onClick={load} data-testid="delivery-refresh" className="p-2 border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          {loading ? (
            <div className="p-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="delivery-empty">No messages match this filter yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
                  <th className="py-3 px-3">When</th><th className="py-3 px-3">Channel</th><th className="py-3 px-3">To</th>
                  <th className="py-3 px-3">Subject</th><th className="py-3 px-3">Status</th><th className="py-3 px-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`delivery-row-${r.id}`}>
                    <td className="py-2.5 px-3 whitespace-nowrap text-slate-600">{fmt(r.created_at)}</td>
                    <td className="py-2.5 px-3 text-slate-600">{r.channel === 'sms' ? <span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> SMS</span> : <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</span>}</td>
                    <td className="py-2.5 px-3 text-slate-900 font-medium">{r.to}</td>
                    <td className="py-2.5 px-3 text-slate-700 max-w-xs truncate" title={r.subject}>{r.subject}</td>
                    <td className="py-2.5 px-3">
                      {r.status === 'sent'
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-widest"><CheckCircle2 className="w-3 h-3" /> Sent</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-widest"><AlertTriangle className="w-3 h-3" /> Failed</span>}
                      {r.webhook_status && <div className={`mt-1 text-[10px] font-semibold uppercase tracking-widest ${['bounced', 'complained'].includes(r.webhook_status) ? 'text-red-600' : 'text-slate-500'}`} data-testid={`delivery-webhook-${r.id}`}>→ {r.webhook_status}</div>}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-red-700 max-w-md">{r.error || <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDeliveryLogs;
